import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Image } from 'react-native';
import { useLanding } from '@/components/Landing/LandingKit';
import useMacDownloadRelease from '@/hooks/useMacDownloadRelease';
import '@/app/styles/mac-install.css';

const DownloadContext = createContext(undefined);
export const useMacDownload = () => useContext(DownloadContext)?.showSetup;
export const useMacDownloadDetails = () => useContext(DownloadContext)?.download;

export function MacInstallIllustration() {
  return <div className="mac-install-illustration" role="img" aria-label="Drag the Gab AI app into the Applications folder.">
    <div className="mac-install-window-title" aria-hidden="true"><span>Gab AI</span><span>Install</span></div>
    <div className="mac-install-drag" aria-hidden="true">
      <div className="mac-install-item"><Image source={require('../../macos/gabaiapp-macOS/Assets.xcassets/AppIcon.appiconset/icon_256x256.png')} accessible={false} style={{ width: 72, height: 72, margin: 4 }} /><span>Gab AI</span></div>
      <svg className="mac-install-arrow" width="66" height="28" viewBox="0 0 66 28" fill="none"><path d="M2 14h58M49 3l12 11-12 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
      <div className="mac-install-item"><svg className="mac-install-folder" width="80" height="80" viewBox="0 0 80 80" fill="none"><path d="M7 19a6 6 0 0 1 6-6h20l8 8h26a6 6 0 0 1 6 6v34a6 6 0 0 1-6 6H13a6 6 0 0 1-6-6Z" fill="#D5E9DB" stroke="#719581" strokeWidth="1.5" /><path d="M8 30h64" stroke="#719581" strokeWidth="1.5" /><path d="m32 54 8-17 8 17m-19-5h22" stroke="#123524" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" /></svg><span>Applications</span></div>
    </div>
    <p aria-hidden="true">Drag over. Settle in.</p>
  </div>;
}

export default function MacDownloadProvider({ children }) {
  const { c } = useLanding();
  const download = useMacDownloadRelease();
  const { url } = download;
  const dialog = useRef(null);
  const trigger = useRef(null);
  const [open, setOpen] = useState(false);
  const isZip = /\.zip(?:[?#]|$)/i.test(url || '');
  useEffect(() => {
    if (!open) return undefined;
    const element = dialog.current;
    element.showModal();
    return () => { if (element.open) element.close(); };
  }, [open]);
  function close() {
    dialog.current?.close();
    setOpen(false);
    trigger.current?.focus({ preventScroll: true });
  }
  function showSetup(event) {
    // The link continues its normal download; never claim that it completed.
    trigger.current = event?.currentTarget || document.activeElement;
    setOpen(true);
  }
  return <DownloadContext.Provider value={{ showSetup, download }}>
    {children}
    <dialog ref={dialog} className="mac-install-dialog" aria-labelledby="mac-install-title" aria-describedby="mac-install-intro"
      style={{ '--install-paper': c.page, '--install-ink': c.ink, '--install-copy': c.inkSoft, '--install-rule': c.ruleStrong, '--install-accent': c.accent, '--install-action': c.accentFill }}
      onKeyDown={event => { if (event.key === 'Escape') event.stopPropagation(); }}
      onCancel={event => { event.preventDefault(); close(); }} onClose={() => setOpen(false)}
      onClick={event => { if (event.target === dialog.current) { const r = dialog.current.getBoundingClientRect(); if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) close(); } }}>
      <button type="button" className="mac-install-close" aria-label="Close setup instructions" onClick={close}>×</button>
      <div className="mac-install-heading">
        <span className="mac-install-eyebrow">WELCOME TO GAB AI FOR MAC</span>
        <h2 id="mac-install-title">A little setup.<br /><em>A lot possible.</em></h2>
        <p id="mac-install-intro">Your download should begin shortly. Here’s how to make yourself at home.</p>
      </div>
      <div className="mac-install-body">
        <MacInstallIllustration />
        <ol className="mac-install-steps">
          <li><span>1</span><div><h3>Open your download</h3><p>Find the Gab AI <strong>{isZip ? '.zip' : '.dmg'}</strong> file in Downloads and double-click it{isZip ? ' to unzip the app.' : ' to open the installer.'}</p></div></li>
          <li><span>2</span><div><h3>Drag into Applications</h3><p>{isZip ? 'Drag the extracted Gab AI app into Applications in the Finder sidebar.' : 'Drag Gab AI onto the Applications folder in the installer window. Wait for the copy to finish.'}</p></div></li>
          <li><span>3</span><div><h3>Open Gab AI. Make it yours.</h3><p>Open Gab AI from Applications. Choose <strong>Open</strong> if macOS asks, then sign in to your account.</p></div></li>
        </ol>
      </div>
      <div className="mac-install-note">{isZip ? 'Once the app is in Applications, you can delete the ZIP.' : 'Once Gab AI is in Applications, eject the installer in Finder. You can then delete the DMG.'} Connect folders and browser access when you’re ready.</div>
      <footer className="mac-install-footer">
        <div><span>Download didn’t start?</span> <a href={url || undefined} download target="_blank" rel="noopener noreferrer">Try again</a><a href="/docs/mac-app">Setup guide ↗</a></div>
        <button type="button" className="mac-install-done" onClick={close}>Got it</button>
      </footer>
    </dialog>
  </DownloadContext.Provider>;
}
